import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Inbox, ChevronRight, Pencil, Trash2, Plus } from "lucide-react";

import {
  listVenues,
  createVenue,
  updateVenue,
  deleteVenue,
  toggleVenueStatus,
} from "./venuesApi";
import { getContactEnquirySummary } from "./contactEnquiriesApi";

import VenueForm from "./VenueForm";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import FormButton from "../../../components/form/FormButton";
import useTableController from "../../../components/table/useTableController";
import TableToolbar from "../../../components/table/TableToolbar";
import Paginator from "../../../components/table/Paginator";

import "./venue.css";
import "./contactEnquiries.css";

const tagClass = (tag) => {
  const key = String(tag || "").toLowerCase();

  if (key === "featured") return "sa-tag sa-tag--featured";
  if (key === "premium") return "sa-tag sa-tag--premium";
  if (key === "popular") return "sa-tag sa-tag--popular";

  return "sa-tag";
};

const VenuesList = () => {
  const navigate = useNavigate();

  const isVenueAdmin = window.location.pathname.startsWith("/venue-admin");
  const isCountryAdmin = window.location.pathname.startsWith("/country-admin");

  const basePath = isVenueAdmin
    ? "/venue-admin"
    : isCountryAdmin
    ? "/country-admin"
    : "/super-admin";

  const isSuperAdmin = basePath === "/super-admin";

  const [raw, setRaw] = useState([]);
  const [newEnquiryCount, setNewEnquiryCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const [busyId, setBusyId] = useState(null);

  const [filters, setFilters] = useState({
    status: "all",
    tag: "all",
    city: "all",
  });

  const load = async () => {
    setLoading(true);

    try {
      const data = await listVenues();

      const rows = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];

      setRaw(rows);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load venues");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;

    let active = true;
    getContactEnquirySummary()
      .then((data) => {
        if (active) setNewEnquiryCount(Number(data?.new || 0));
      })
      .catch((error) => {
        // The venue list should remain fully usable if the optional badge fails.
        console.error("getContactEnquirySummary error:", error);
      });

    return () => {
      active = false;
    };
  }, [isSuperAdmin]);

  const tableItems = useMemo(() => {
    return (raw || []).map((venue) => ({
      ...venue,
      city: venue?.location?.city || "",
      country: venue?.location?.country || "",
      tagsStr: Array.isArray(venue?.tags) ? venue.tags.join(" ") : "",
      groundCountSafe:
        venue?.groundCount ??
        (Array.isArray(venue?.grounds) ? venue.grounds.length : 0),
    }));
  }, [raw]);

  const cityOptions = useMemo(() => {
    const citySet = new Set(
      (tableItems || []).map((item) => item.city).filter(Boolean)
    );

    return Array.from(citySet).sort();
  }, [tableItems]);

  const filterDefs = useMemo(
    () => [
      {
        id: "status",
        label: "Status",
        options: [
          { value: "all", label: "All" },
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ],
      },
      {
        id: "tag",
        label: "Tags",
        options: [
          { value: "all", label: "All" },
          { value: "featured", label: "Featured" },
          { value: "premium", label: "Premium" },
          { value: "popular", label: "Popular" },
        ],
      },
      {
        id: "city",
        label: "City",
        options: [{ value: "all", label: "All" }].concat(
          cityOptions.map((city) => ({ value: city, label: city }))
        ),
      },
    ],
    [cityOptions]
  );

  const filteredItems = useMemo(() => {
    if (isVenueAdmin) return tableItems || [];

    return (tableItems || []).filter((item) => {
      if (filters.status === "active" && !item.active) return false;
      if (filters.status === "inactive" && !!item.active) return false;

      if (filters.tag !== "all") {
        const tags = String(item.tagsStr || "")
          .split(" ")
          .filter(Boolean);

        if (!tags.includes(filters.tag)) return false;
      }

      if (filters.city !== "all" && item.city !== filters.city) return false;

      return true;
    });
  }, [tableItems, filters, isVenueAdmin]);

  const tc = useTableController(filteredItems, {
    searchKeys: isVenueAdmin ? [] : ["name", "city", "country", "tagsStr"],
    initialPageSize: 10,
  });

  useEffect(() => {
    tc.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const sortCaret = (key) => {
    if (tc.sortKey !== key) return null;
    return tc.sortDir === "asc" ? " ▲" : " ▼";
  };

  const ThSort = ({ keyName, children, style }) => (
    <th
      style={{ cursor: "pointer", userSelect: "none", ...(style || {}) }}
      onClick={() => tc.toggleSort(keyName)}
      title={`Sort by ${keyName}`}
    >
      {children}
      <span style={{ opacity: 0.8 }}>{sortCaret(keyName)}</span>
    </th>
  );

  const serialStart = (tc.page - 1) * tc.pageSize;

  const handleCreate = async (payload) => {
    try {
      const response = await createVenue(payload);
      const venue = response?.venue || response;
      const venueId = venue?._id || venue?.id;

      toast.success("Venue created");
      setCreating(false);

      await load();

      if (venueId) {
        navigate(`/super-admin/venues/${venueId}`);
      }
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Create failed";
      toast.error(message);
      throw error;
    }
  };

  const handleUpdate = async (payload) => {
    try {
      const id = editing?._id || editing?.id;

      await updateVenue(id, payload);
      toast.success("Venue updated");
      setEditing(null);
      await load();
    } catch (error) {
      const message = error?.response?.data?.message || "Update failed";
      toast.error(message);
      throw error;
    }
  };

  const handleDelete = async () => {
    const id = deleting?._id || deleting?.id;

    if (!id) {
      toast.error("Missing id");
      return;
    }

    try {
      setBusyId(id);
      await deleteVenue(id);
      toast.success("Venue deleted");
      setDeleting(null);
      await load();
    } catch (error) {
      const message = error?.response?.data?.message || "Delete failed";
      toast.error(message);
    } finally {
      setBusyId(null);
    }
  };

  const handleToggle = async (venue) => {
    const id = venue?._id || venue?.id;
    const next = !venue.active;

    setRaw((prev) =>
      prev.map((item) => ((item._id || item.id) === id ? { ...item, active: next } : item))
    );

    setBusyId(id);

    try {
      await toggleVenueStatus(id, next);
    } catch (error) {
      setRaw((prev) =>
        prev.map((item) =>
          (item._id || item.id) === id ? { ...item, active: venue.active } : item
        )
      );

      const message = error?.response?.data?.message || "Status change failed";
      toast.error(message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="sa-head-row venues-head-row">
        <h1 className="sa-h1 sa-topbar-managed-page-title">Venues</h1>

        {!isVenueAdmin && (
          <div className="venues-head-actions">
            {isSuperAdmin && (
              <button
                type="button"
                className="venues-enquiries-card"
                onClick={() => navigate("/super-admin/contact-enquiries")}
                aria-label={
                  newEnquiryCount > 0
                    ? `Contact Enquiries, ${newEnquiryCount} new`
                    : "Contact Enquiries"
                }
              >
                <span className="venues-enquiries-icon" aria-hidden="true">
                  <Inbox className="lucide" />
                </span>

                <span className="venues-enquiries-copy">
                  <strong>Contact Enquiries</strong>
                  <small>
                    {newEnquiryCount > 0
                      ? `${newEnquiryCount} new ${newEnquiryCount === 1 ? "enquiry" : "enquiries"}`
                      : "Open enquiry inbox"}
                  </small>
                </span>

                {newEnquiryCount > 0 && (
                  <span className="venues-enquiries-badge" aria-hidden="true">
                    {newEnquiryCount > 99 ? "99+" : newEnquiryCount}
                  </span>
                )}

                <ChevronRight className="venues-enquiries-chevron" aria-hidden="true" />
              </button>
            )}

            <FormButton
              variant="primary"
              className="venues-new-btn"
              onClick={() => setCreating(true)}
            >
              <Plus className="lucide" /> Add Venue
            </FormButton>
          </div>
        )}
      </div>

      {!isVenueAdmin && (
        <TableToolbar
          searchTerm={tc.searchTerm}
          onSearchChange={tc.setSearch}
          filterDefs={filterDefs}
          filters={filters}
          onFilterChange={(id, value) =>
            setFilters((prev) => ({ ...prev, [id]: value }))
          }
          searchPlaceholder="Search venues..."
        />
      )}

      <div className="sa-card sa-card--venues-page">
        {loading ? (
          <div className="sa-placeholder" style={{ height: 120 }}>
            Loading…
          </div>
        ) : tc.rows.length === 0 ? (
          <div className="sa-empty">
            <div>No venues {tc.searchTerm ? "match your search" : "yet"}.</div>

            {!isVenueAdmin && (
              <FormButton variant="primary" onClick={() => setCreating(true)}>
                Add your first venue
              </FormButton>
            )}
          </div>
        ) : (
          <>
            <div className="sa-table-wrap venues-table-wrap">
              <table className="sa-table venues-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>S.N.</th>
                    <ThSort keyName="name">Venue</ThSort>
                    <ThSort keyName="city">City / Country</ThSort>
                    <ThSort keyName="groundCountSafe"># Grounds</ThSort>
                    <th>Tags</th>
                    {!isVenueAdmin && <ThSort keyName="active">Status</ThSort>}
                    <th style={{ textAlign: "right", width: 110 }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {tc.rows.map((venue, index) => {
                    const id = venue._id || venue.id;

                    return (
                      <tr key={id || venue.slug || `${venue.name}-${index}`}>
                        <td>{serialStart + index + 1}</td>

                        <td className="sa-cell-name">
                          <button
                            type="button"
                            className="sa-name-link pretty"
                            title="View details"
                            onClick={() => navigate(`${basePath}/venues/${id}`)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                navigate(`${basePath}/venues/${id}`);
                              }
                            }}
                          >
                            {venue.name}
                            <span className="caret" aria-hidden="true">
                              ↗
                            </span>
                          </button>
                        </td>

                        <td>
                          {venue.city || "—"}, {venue.country || "—"}
                        </td>

                        <td style={{ textAlign: "center" }}>
                          {venue.groundCountSafe}
                        </td>

                        <td>
                          {venue.tagsStr ? (
                            <div className="sa-tags">
                              {venue.tagsStr
                                .split(" ")
                                .filter(Boolean)
                                .map((tag) => (
                                  <span key={tag} className={tagClass(tag)}>
                                    {tag}
                                  </span>
                                ))}
                            </div>
                          ) : (
                            <span className="sa-gray">—</span>
                          )}
                        </td>

                        {!isVenueAdmin && (
                          <td>
                            <div className="sa-status-wrap">
                              <label className="sa-switch" title="Toggle status">
                                <input
                                  type="checkbox"
                                  checked={!!venue.active}
                                  onChange={() => handleToggle(venue)}
                                  disabled={busyId === id}
                                />
                                <span />
                              </label>

                              <span
                                className={`sa-status ${
                                  venue.active ? "active" : "inactive"
                                }`}
                              >
                                {venue.active ? "Active" : "Inactive"}
                              </span>
                            </div>
                          </td>
                        )}

                        <td style={{ textAlign: "right" }}>
                          <button
                            type="button"
                            className="sa-icon-link"
                            onClick={() => setEditing(venue)}
                            title="Edit venue"
                            aria-label="Edit venue"
                          >
                            <Pencil className="lucide" />
                          </button>

                          <button
                            type="button"
                            className={`sa-icon-link danger ${
                              isVenueAdmin ? "sa-icon-link--disabled" : ""
                            }`}
                            onClick={() => {
                              if (isVenueAdmin) return;
                              setDeleting(venue);
                            }}
                            disabled={isVenueAdmin || busyId === id}
                            title={
                              isVenueAdmin
                                ? "Venue admins cannot delete venues"
                                : "Delete venue"
                            }
                            aria-label="Delete venue"
                          >
                            <Trash2 className="lucide" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Paginator
              page={tc.page}
              totalPages={tc.totalPages}
              onPage={tc.setPage}
              pageSize={tc.pageSize}
              onPageSizeChange={tc.setPageSize}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          </>
        )}
      </div>

      {!isVenueAdmin && creating && (
        <VenueForm
          open={creating}
          mode="create"
          onCancel={() => setCreating(false)}
          onSubmit={handleCreate}
        />
      )}

      {editing && (
        <VenueForm
          key={editing?._id || editing?.id}
          open={!!editing}
          mode="edit"
          initialData={{
            name: editing?.name || "",
            shortDescription: editing?.shortDescription || "",
            fullDescription: editing?.fullDescription || "",

            location: {
              address: editing?.location?.address || "",
              city: editing?.location?.city || "",
              country: editing?.location?.country || "",
              latitude: editing?.location?.latitude ?? "",
              longitude: editing?.location?.longitude ?? "",
            },

            media: {
              bannerImage: editing?.media?.bannerImage || "",
              bannerKey: editing?.media?.bannerKey || "",
              gallery: Array.isArray(editing?.media?.gallery)
                ? editing.media.gallery
                : [],
              carouselKeys: Array.isArray(editing?.media?.carouselKeys)
                ? editing.media.carouselKeys
                : [],
            },

            contact: editing?.contact || null,
            contactNumber: editing?.contactNumber || "",

            facilities: Array.isArray(editing?.facilities)
              ? editing.facilities
              : [],

            tags: Array.isArray(editing?.tags) ? editing.tags : [],

            policies: {
              cancellationPolicy: editing?.policies?.cancellationPolicy || "",
              rulesTerms: editing?.policies?.rulesTerms || "",
              openTime: editing?.policies?.openTime || "06:00",
              closeTime: editing?.policies?.closeTime || "22:00",
            },

            googleMapsUrl: editing?.googleMapsUrl || "",

            pricing: {
              basePrice: editing?.pricing?.basePrice ?? "",
            },

            active: !!editing?.active,
          }}
          onCancel={() => setEditing(null)}
          onSubmit={handleUpdate}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title="Delete Venue"
        message={`Are you sure you want to delete "${
          deleting?.name || "this venue"
        }"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={busyId === (deleting?._id || deleting?.id)}
        onConfirm={handleDelete}
        onCancel={() => {
          if (busyId !== (deleting?._id || deleting?.id)) {
            setDeleting(null);
          }
        }}
      />
    </>
  );
};

export default VenuesList;